import { useCallback, useEffect, useMemo, useRef, useImperativeHandle, forwardRef } from 'react';
import { useEditor, EditorContent, type Editor } from '@tiptap/react';
import { cn } from '@/lib/utils';
import { createMarkdownExtensions } from '../../editor/markdownExtensions';

export interface MarkdownEditorHandle {
  focus: () => void;
}

interface MarkdownEditorProps {
  value: string;
  onChange: (value: string) => void;
  onBlur?: (value: string) => void;
  placeholder?: string;
  className?: string;
}

type EditorWithMarkdown = Editor & { getMarkdown?: () => string };

/**
 * Bear-style markdown editor using TipTap.
 * Provides WYSIWYG editing with markdown shortcuts and live preview.
 * Markdown syntax is rendered as formatted text while you type.
 */
export const MarkdownEditor = forwardRef<MarkdownEditorHandle, MarkdownEditorProps>(function MarkdownEditor(
  { value, onChange, onBlur, placeholder, className },
  ref
) {
  const onBlurRef = useRef(onBlur);
  const onChangeRef = useRef(onChange);
  const isFocusedRef = useRef(false);
  const lastMarkdownRef = useRef(value ?? '');
  const containerRef = useRef<HTMLDivElement | null>(null);
  const wasFocusedBeforeClickRef = useRef(false);
  const extensions = useMemo(
    () => createMarkdownExtensions({ placeholder }),
    [placeholder]
  );

  // Keep refs up to date
  useEffect(() => {
    onBlurRef.current = onBlur;
    onChangeRef.current = onChange;
  }, [onBlur, onChange]);

  useEffect(() => {
    lastMarkdownRef.current = value ?? '';
  }, [value]);

  const editorRef = useRef<Editor | null>(null);
  const getMarkdownFromEditor = useCallback(
    (editorInstance?: Editor | null) => {
      const instance = editorInstance ?? editorRef.current;
      if (!instance) {
        return '';
      }

      const markdownGetter = instance as EditorWithMarkdown;
      let markdown =
        typeof markdownGetter.getMarkdown === 'function'
          ? markdownGetter.getMarkdown()
          : '';

      // Fallback for environments where the Markdown extension can't serialize
      // the current schema (for example, older Tiptap core extensions without
      // markdown renderers). In that case, fall back to plain text so we still
      // persist user input.
      if (!markdown) {
        try {
          markdown = instance.getText({ blockSeparator: '\n\n' });
        } catch {
          markdown = instance.getText?.() ?? '';
        }
      }

      return markdown;
    },
    []
  );

  const commitPendingBlur = useCallback(
    (editorInstance?: Editor | null) => {
      if (!isFocusedRef.current) {
        return;
      }
      const latestMarkdown = getMarkdownFromEditor(editorInstance) || lastMarkdownRef.current;
      lastMarkdownRef.current = latestMarkdown;
      isFocusedRef.current = false;
      onBlurRef.current?.(latestMarkdown);
    },
    [getMarkdownFromEditor]
  );

  const editor = useEditor(
    {
      extensions,
      content: value,
      contentType: 'markdown',
      onUpdate: ({ editor: editorInstance }) => {
        const markdown = getMarkdownFromEditor(editorInstance);
        lastMarkdownRef.current = markdown;
        onChangeRef.current(markdown);
      },
      onFocus: () => {
        isFocusedRef.current = true;
      },
      onBlur: ({ editor: editorInstance }) => {
        commitPendingBlur(editorInstance);
      },
      editorProps: {
        attributes: {
          class:
            'markdown-editor-content prose prose-sm max-w-none min-h-[160px] px-4 py-3 focus:outline-none text-sm leading-relaxed',
        },
        handleClick: (_view, _pos, event) => {
          const anchor = (event.target as HTMLElement).closest('a');
          if (!anchor) return false;
          const href = anchor.getAttribute('href');
          if (!href) return false;
          // When editor was not focused before this click, plain click opens the link.
          // When already focused (actively editing), require Cmd/Ctrl+click.
          if (!wasFocusedBeforeClickRef.current || event.metaKey || event.ctrlKey) {
            event.preventDefault();
            window.open(href, '_blank', 'noopener,noreferrer');
            return true;
          }
          return false;
        },
      },
    },
    [extensions, commitPendingBlur, getMarkdownFromEditor]
  );
  editorRef.current = editor;

  // Expose focus method via ref
  useImperativeHandle(ref, () => ({
    focus: () => {
      editor?.commands.focus();
    },
  }), [editor]);

  // Capture whether the editor was already focused before a click lands.
  // TipTap's handleClick fires after focus, so we snapshot this on mousedown.
  useEffect(() => {
    const container = containerRef.current;
    if (!container || !editor) return;
    const onMouseDown = () => {
      wasFocusedBeforeClickRef.current = editor.isFocused;
    };
    container.addEventListener('mousedown', onMouseDown, true);
    return () => container.removeEventListener('mousedown', onMouseDown, true);
  }, [editor]);

  // Force a blur if the user clicks outside of the editor but the browser keeps
  // the contenteditable focused (common when clicking on non-focusable elements).
  useEffect(() => {
    if (!editor) {
      return;
    }

    const handlePointerDown = (event: PointerEvent | MouseEvent) => {
      const target = event.target as Node | null;
      if (!containerRef.current || !target) {
        return;
      }
      if (containerRef.current.contains(target)) {
        return;
      }
      if (editor.isFocused) {
        commitPendingBlur(editor);
        editor.commands.blur();
      }
    };

    document.addEventListener('pointerdown', handlePointerDown, true);
    return () => {
      document.removeEventListener('pointerdown', handlePointerDown, true);
    };
  }, [commitPendingBlur, editor]);

  useEffect(() => {
    return () => {
      commitPendingBlur();
    };
  }, [commitPendingBlur]);

  // Update editor content when value prop changes (but only if different from current)
  // Skip update if editor is focused to avoid interrupting user input
  useEffect(() => {
    if (!editor) {
      return;
    }

    // Don't update if editor is focused - user is actively editing
    if (editor.isFocused) {
      return;
    }

    const currentMarkdown = getMarkdownFromEditor(editor);
    // Normalize both strings for comparison (trim whitespace)
    const normalizedCurrent = currentMarkdown.trim();
    const normalizedValue = (value || '').trim();

    if (normalizedCurrent !== normalizedValue) {
      editor.commands.setContent(value || '', { contentType: 'markdown' });
      lastMarkdownRef.current = value || '';
    }
  }, [editor, value, getMarkdownFromEditor]);

  return (
    <div
      ref={containerRef}
      className={cn('rounded-2xl border border-border/60 bg-card overflow-hidden', className)}
      onClick={(e) => e.stopPropagation()}
    >
      <EditorContent editor={editor} />
    </div>
  );
});
